import {
  Action,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import {HttpClient} from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {IUser} from '../../interfaces/user.interface';
import {tap, catchError} from 'rxjs/operators';
import {Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {CookiesService} from '../../services/cookies.service';
import * as Act from './auth.actions';
import {TokensEnum} from '../../interfaces/TokensEnum';
import {endpoints} from '../../constants/endpoints';
import {IPharmacy} from '../../interfaces/pharmacy.interface';
import {GetUserPharmacies} from '../pharmacy/pharmacy.actions';
import {StateClear} from 'ngxs-reset-plugin';

export interface AuthStateModel {
  loading: boolean;
  success: boolean;
  userId: string;
  companyId: string;
  pharmacyId: string;
  pharmacies: IPharmacy[];
  accessToken: string;
  currentUser: IUser;
  activeUserIsLoggedInUser: boolean;
  activeUser: IUser;
  lockScreen: boolean;
  signUpResponse: any;
  userHasPinResponse: boolean;
  validateInvitationResponse: any;
  validatePasswordLinkResponse: any;
}

@State<AuthStateModel>({
  name: 'auth',
})

@Injectable()
export class AuthState implements NgxsOnInit {
  static USER_STORAGE_KEY = 'C2KU';

  endpoints = endpoints;

  constructor(
    private store: Store,
    private ngZone: NgZone,
    private router: Router,
    private http: HttpClient,
    private cookiesService: CookiesService,
  ) {
  }

  @Selector()
  static loading(state: AuthStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: AuthStateModel): boolean {
    return state.success;
  }

  @Selector()
  static accessToken(state: AuthStateModel): string {
    return state.accessToken;
  }

  @Selector()
  static bearerToken(state: AuthStateModel): string {
    return 'Bearer ' + state.accessToken;
  }


  @Selector()
  static userId(state: AuthStateModel): string {
    return state.userId;
  }

  @Selector()
  static activeUserId(state: AuthStateModel): string {
    return state.activeUserIsLoggedInUser ? state.currentUser.id : state.activeUser.id;
  }

  @Selector()
  static pharmacyId(state: AuthStateModel): string {
    return state.pharmacyId;
  }

  @Selector()
  static currentUser(state: AuthStateModel): IUser {
    return state.currentUser;
  }

  @Selector()
  static lockScreen(state: AuthStateModel): boolean {
    return state.lockScreen;
  }

  @Selector()
  static signUpResponse(state: AuthStateModel): any {
    return state.signUpResponse;
  }

  @Selector()
  static userHasPinResponse(state: AuthStateModel): any {
    return state.userHasPinResponse;
  }

  @Selector()
  static activeUserIsLoggedInUser(state: AuthStateModel): boolean {
    return state.activeUserIsLoggedInUser;
  }

  @Selector()
  static activeUser(state: AuthStateModel): IUser {
    return state.activeUserIsLoggedInUser ? state.currentUser : state.activeUser;
  }

  @Selector()
  static validateInvitationResponse(state: AuthStateModel): string {
    return state.validateInvitationResponse;
  }

  @Selector()
  static validatePasswordLinkResponse(state: AuthStateModel): string {
    return state.validatePasswordLinkResponse;
  }

  ngxsOnInit(ctx: StateContext<AuthStateModel>): void {
    const accessToken: string | null = this.cookiesService.getCookies(TokensEnum.AccessToken);
    const userId: string | null = this.cookiesService.getCookies(TokensEnum.UserId);
    const companyId: string | null = this.cookiesService.getCookies(TokensEnum.CompanyId);
    const pharmacyId: string | null = this.cookiesService.getCookies(TokensEnum.PharmacyId);

    if (accessToken) {
      ctx.patchState({
        accessToken,
        userId,
        companyId,
        pharmacyId,
        activeUserIsLoggedInUser: true
      });

      this.store.dispatch(new Act.LoadUserFromLocalStorage());

      this.getInitialData();
    }
  }

  @Action(Act.SignUp)
  signUp(ctx: StateContext<AuthStateModel>, {signUpFormValue}: Act.SignUp): Observable<any> {
    ctx.patchState({loading: true, signUpResponse: null});
    return this.http.post(this.endpoints.account.signUp, signUpFormValue)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({loading: false, activeUserIsLoggedInUser: true});
          this.cookiesService.deleteCookie(TokensEnum.LockScreen);
          this.handleSignUpResponse(responseData, ctx);
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.InvitedSignUp)
  invitedSignUp(ctx: StateContext<AuthStateModel>, {signUpFormValue}: Act.InvitedSignUp): Observable<any> {
    ctx.patchState({loading: true, signUpResponse: null});

    return this.http.post(this.endpoints.account.invitedSignUp, signUpFormValue)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({loading: false});
          if (responseData.success) {
            this.cookiesService.deleteCookie(TokensEnum.LockScreen);
            this.cookiesService.deleteCookie(TokensEnum.LockScreen);
            this.cookiesService.deleteCookie(TokensEnum.AccessToken);
            this.cookiesService.deleteCookie(TokensEnum.UserId);
            this.cookiesService.deleteCookie(TokensEnum.CompanyId);
            this.cookiesService.deleteCookie(TokensEnum.PharmacyId);
            this.removeUserFromLocalStorage();

            // this.store.dispatch(new StateResetAll());
            // TODO: Make sure this is ok - I asssume there's a reason we initially used Reset vs Clear, but I don't see an InitialState set
            this.store.dispatch(new StateClear());
            this.handleSuccessfulInvitedSignUpResponse(responseData, ctx);
          } else {
            ctx.patchState({signUpResponse: responseData});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.Login)
  login(ctx: StateContext<AuthStateModel>, {loginData}: Act.Login): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.post(endpoints.auth.login, loginData)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({loading: false});
          this.cookiesService.deleteCookie(TokensEnum.LockScreen);
          this.cookiesService.deleteCookie(TokensEnum.AccessToken);
          this.cookiesService.deleteCookie(TokensEnum.UserId);
          this.cookiesService.deleteCookie(TokensEnum.CompanyId);
          this.cookiesService.deleteCookie(TokensEnum.PharmacyId);
          this.removeUserFromLocalStorage();

          this.store.dispatch(new StateClear());
          if (responseData.value && responseData.value.token) {
            this.cookiesService.deleteCookie(TokensEnum.LockScreen);
            this.saveUserTokens(responseData, ctx);
            this.store.dispatch(new Act.SetActiveUser(responseData.value.user));
          } else {
            this.navigate('auth/login');
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CheckUser)
  checkUser(ctx: StateContext<AuthStateModel>, {loginData}: Act.CheckUser): Observable<any> {
    ctx.patchState({loading: true, success: undefined});
    loginData.pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    return this.http.post(endpoints.user.validateCredentials, loginData)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: (responseData?.success ?? false)
                && !!responseData.value && !!responseData.value.token,
          });
          if (responseData?.success ?? false) {
            this.store.dispatch(new Act.SetActiveUser(responseData.value.user));
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, success: false});
          return of(null);
        }),
      );
  }


  @Action(Act.UserHasPin)
  userHasPin(ctx: StateContext<AuthStateModel>, {}: Act.UserHasPin): Observable<any> {
    ctx.patchState({loading: true, success: undefined});

    return this.http.get(endpoints.account.userHasPIN)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            userHasPinResponse: (responseData && responseData.code === 200) ? true : false
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ValidateInvitation)
  validateInvitation(ctx: StateContext<AuthStateModel>, {Email, Password}: Act.ValidateInvitation): Observable<any> {
    ctx.patchState({loading: true, validateInvitationResponse: ''});

    return this.http.post(endpoints.auth.validateInvitation, {Email, Password})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({loading: false});
          if (resp.code === 403) {
            ctx.patchState({validateInvitationResponse: 'Expired'});
          } else if (resp.code === 200) {
            ctx.patchState({validateInvitationResponse: 'OK'});
          } else {
            ctx.patchState({validateInvitationResponse: 'Exists'});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, validateInvitationResponse: 'Expired'});
          return of(null);
        }),
      );
  }

  @Action(Act.ValidatePasswordLink)
  validatePasswordLink(ctx: StateContext<AuthStateModel>, {Email, Code}: Act.ValidatePasswordLink): Observable<any> {
    ctx.patchState({loading: true, validatePasswordLinkResponse: ''});

    return this.http.post(endpoints.auth.validatePasswordLink, {Email, Code})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({loading: false});
          if (resp.code === 200 && resp.success === true) {
            ctx.patchState({validatePasswordLinkResponse: 'OK'});
          } else {
            ctx.patchState({validatePasswordLinkResponse: 'Unkown State'});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false, validatePasswordLinkResponse: 'Expired'});
          return of(null);
        }),
      );
  }

  @Action(Act.SetPharmacyId)
  setPharmacyId(ctx: StateContext<AuthStateModel>, {pharmacyId}: Act.SetPharmacyId): void {
    this.cookiesService.setCookies(TokensEnum.PharmacyId, pharmacyId, 1);
    ctx.patchState({pharmacyId});
  }

  @Action(Act.RequestResetByEmail)
  requestResetByEmail(ctx: StateContext<AuthStateModel>, {email}: Act.RequestResetByEmail): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.post(endpoints.auth.resetPassword, {email})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.ResetPassword)
  resetPassword(ctx: StateContext<AuthStateModel>, {resetData}: Act.ResetPassword): Observable<any> {
    ctx.patchState({loading: true});

    return this.http.post(endpoints.auth.confirmResetPassword, resetData)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({loading: false});

          if (responseData.value && responseData.value.token) {
            this.saveUserTokens(responseData, ctx);

            setTimeout(() => {
              this.navigate('dashboard');
            }, 1000);
          } else {
            this.navigate('auth/login');
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CloseUserAccount)
  closeUserAccount(ctx: StateContext<AuthStateModel>, {}: Act.CloseUserAccount): Observable<any> {
    ctx.patchState({loading: true});

    // TODO: replace with real endpoint
    // return this.http.post(endpoints.auth.closeAccount)
    return of({})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
            success: responseData.success,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateUserAccount)
  updateUserAccount(ctx: StateContext<AuthStateModel>, {user}: Act.UpdateUserAccount): void {
    const currentUser = ctx.getState().currentUser;
    const spreadUser: any  = user;
    delete spreadUser.currentPassword;
    delete spreadUser.newPassword;
    ctx.patchState({currentUser: {...currentUser, ...spreadUser}});
  }

  @Action(Act.LockSystem)
  lockSystem(ctx: StateContext<AuthStateModel>, {lock}: Act.LockSystem): void {
    this.cookiesService.setCookies(TokensEnum.LockScreen, JSON.stringify(lock), -1);

    ctx.patchState({lockScreen: lock});

    if (lock) {
      // if we're locking the screen, set this to false
      ctx.patchState({activeUserIsLoggedInUser: false});
    }

  }

  @Action(Act.HideAlibiWarning)
  hideAlibiWarning(ctx: StateContext<AuthStateModel>, {}: Act.HideAlibiWarning): Observable<any> {
    // TODO: replace with real endpoint
    // return this.http.post(endpoints.auth.closeAccount)
    return of({})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            // currentUser: responseData.value,
          });
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  @Action(Act.Logout)
  logout(ctx: StateContext<AuthStateModel>): void {
    this.clearStateAndCookies();
    this.navigate('/auth/login');
  }

  @Action(Act.SetActiveUser)
  setUnlockedByUser(ctx: StateContext<AuthStateModel>, {pinUser}: Act.SetActiveUser): void {
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    if (pinUser) {
      const activeUserIsLoggedInUser = currentUser?.id === pinUser?.id ?? true;
      ctx.patchState({activeUserIsLoggedInUser, activeUser: activeUserIsLoggedInUser ? currentUser : pinUser});
      this.store.dispatch(new Act.PersistUserToLocalStorage());
    }
  }

  @Action(Act.PersistUserToLocalStorage)
  persistUserToLocalStorage(ctx: StateContext<AuthStateModel>, {}: Act.PersistUserToLocalStorage): void {
    const currentUser = this.store.selectSnapshot(AuthState.currentUser);
    const activeUser = this.store.selectSnapshot(AuthState.activeUser);

    const users = { currentUser, activeUser };
    localStorage.setItem(AuthState.USER_STORAGE_KEY, JSON.stringify(users));
  }

  @Action(Act.ClearStateAndCookies)
  clearStateCookies(ctx: StateContext<AuthStateModel>, {}: Act.ClearStateAndCookies): void {
    this.clearStateAndCookies();
  }

  @Action(Act.LoadUserFromLocalStorage)
  loadUserFromLocalStorage(ctx: StateContext<AuthStateModel>, {}: Act.LoadUserFromLocalStorage): void {
    const userOrUsers = JSON.parse(localStorage.getItem(AuthState.USER_STORAGE_KEY));
    if (userOrUsers) {
      // this is just to make sure the new format works with the
      //  old storage -- after a few days we can remove this check
      //  as only the new format will still be in effect.
      if (userOrUsers.currentUser) {
        ctx.patchState({currentUser: userOrUsers.currentUser});
        let pinUser = userOrUsers.currentUser;
        if (userOrUsers.currentUser && userOrUsers.activeUser && userOrUsers.currentUser.id !== userOrUsers.activeUser.id) {
          pinUser = userOrUsers.activeUser;
        }
        this.store.dispatch(new Act.SetActiveUser(pinUser));
      } else {
        // this is the old format -- load it, and then immediately replace it.
        ctx.patchState({currentUser: userOrUsers});
        this.store.dispatch(new Act.PersistUserToLocalStorage());
      }
    }
  }

  private clearStateAndCookies(): void {
    this.cookiesService.deleteCookie(TokensEnum.LockScreen);
    this.cookiesService.deleteCookie(TokensEnum.AccessToken);
    this.cookiesService.deleteCookie(TokensEnum.UserId);
    this.cookiesService.deleteCookie(TokensEnum.CompanyId);
    this.cookiesService.deleteCookie(TokensEnum.PharmacyId);
    this.removeUserFromLocalStorage();

    // this.store.dispatch(new StateResetAll());
    // TODO: Make sure this is ok - I asssume there's a reason we initially used Reset vs Clear, but I don't see an InitialState set
    this.store.dispatch(new StateClear());
  }

  private removeUserFromLocalStorage(): void {
    localStorage.removeItem(AuthState.USER_STORAGE_KEY);
  }

  private handleSignUpResponse(responseData: any, ctx): void {
    if (responseData.value && responseData.value.token) {
      this.saveUserTokens(responseData, ctx);
      this.navigate('/dashboard/set-up');
    } else if (!responseData.success && responseData.message) {
      ctx.patchState({signUpResponse: responseData});
    }
  }

  private handleSuccessfulInvitedSignUpResponse(responseData: any, ctx): void {
    if (responseData.value && responseData.value.token) {
      ctx.patchState({loggedInUserIsActiveUser: true});
      this.saveUserTokens(responseData, ctx);
      this.navigate('/dashboard');
      setTimeout(() => {
        window.location.reload();
      }, 0);  
    }
  }

  private navigate(route: string): void {
    this.ngZone.run(() => this.router.navigate([route]));
  }

  // get all data required for user interface on initial phase or after reloading
  private getInitialData(): void {
    this.store.dispatch(new GetUserPharmacies());
  }

  private saveUserTokens(responseData, ctx): void {
    const pharmId = responseData.value.user?.pharmacies?.length > 0 ? responseData.value.user.pharmacies[0].id : undefined;
    this.cookiesService.setCookies(TokensEnum.AccessToken, responseData.value.token, 1);
    this.cookiesService.setCookies(TokensEnum.UserId, responseData.value.user?.id, 1);
    this.cookiesService.setCookies(TokensEnum.CompanyId, responseData.value.user?.companyId, 1);
    this.cookiesService.setCookies(TokensEnum.PharmacyId, pharmId, 1);

    ctx.patchState({
      accessToken: responseData.value.token,
      userId: responseData.value.user?.id,
      companyId: responseData.value.user?.companyId,
      pharmacies: responseData.value.user?.pharmacies,
      pharmacyId: pharmId,
      currentUser: responseData.value.user as IUser,
    });

    this.store.dispatch(new Act.PersistUserToLocalStorage());
  }
}
